import { FC, useState, useEffect } from 'react';
import { format, addDays, subDays } from 'date-fns';
import { getDailySchedule, generateSchedule, deleteAssignment, getOutletName, createAssignment, updateAssignment, isSchedulePublished, getSchedulePublicationInfo, publishSchedule, toggleOutlet, logScheduleView, getScheduleViewers } from '../../lib/scheduling';
import type { DailySchedule, OutletCoverage, SchedulePublicationInfo, ScheduleViewer } from '../../types/scheduling';
import { showToast } from '../../hooks/useToast';
import { useRole } from '../../hooks/useRole';

const StaffSchedulerPage: FC = () => {
  const { isAdmin } = useRole();

  // Show tomorrow's schedule if current time is 8PM or later
  const getInitialDate = () => {
    const now = new Date();
    const hour = now.getHours();
    const targetDate = hour >= 20 ? addDays(now, 1) : now;
    return format(targetDate, 'yyyy-MM-dd');
  };

  const [selectedDate, setSelectedDate] = useState<string>(getInitialDate());
  const [schedule, setSchedule] = useState<DailySchedule | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [publicationInfo, setPublicationInfo] = useState<SchedulePublicationInfo | null>(null);
  const [draggedStaff, setDraggedStaff] = useState<{ userId: string; displayName: string; fromOutletId?: number; assignmentId?: string } | null>(null);
  const [dragOverOutletId, setDragOverOutletId] = useState<number | null>(null);
  const [scheduleViewers, setScheduleViewers] = useState<ScheduleViewer[]>([]);
  const [viewersLoading, setViewersLoading] = useState(false);

  useEffect(() => {
    loadSchedule();
  }, [selectedDate]);

  useEffect(() => {
    if (!isPublished || !schedule || schedule.date !== selectedDate) {
      setScheduleViewers([]);
      return;
    }

    let isMounted = true;

    const syncViewers = async () => {
      setViewersLoading(true);
      try {
        await logScheduleView(selectedDate);
        const viewers = await getScheduleViewers(selectedDate);
        if (isMounted) {
          setScheduleViewers(viewers);
        }
      } catch (error) {
        console.error('Failed to sync schedule viewers', error);
      } finally {
        if (isMounted) {
          setViewersLoading(false);
        }
      }
    };

    syncViewers();

    return () => {
      isMounted = false;
    };
  }, [isPublished, selectedDate, schedule]);

  const loadSchedule = async () => {
    setLoading(true);
    try {
      const [data, published, pubInfo] = await Promise.all([
        getDailySchedule(selectedDate, isAdmin),
        isSchedulePublished(selectedDate),
        getSchedulePublicationInfo(selectedDate)
      ]);
      console.log('Schedule data:', data);
      setSchedule(data);
      setIsPublished(published);
      setPublicationInfo(pubInfo);
    } catch (error) {
      showToast('Failed to load schedule', 'error');
      console.error('Schedule load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSchedule = async () => {
    if (!confirm('Generate schedule for this date? This will replace existing assignments.')) {
      return;
    }

    setGenerating(true);
    try {
      await generateSchedule(selectedDate);
      showToast('Schedule generated successfully', 'success');
      await loadSchedule();
    } catch (error) {
      showToast('Failed to generate schedule', 'error');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!confirm('Remove this assignment?')) {
      return;
    }

    try {
      await deleteAssignment(assignmentId);
      showToast('Assignment removed', 'success');
      await loadSchedule();
    } catch (error) {
      showToast('Failed to remove assignment', 'error');
      console.error(error);
    }
  };

  const handlePreviousDay = () => {
    setSelectedDate(format(subDays(new Date(selectedDate), 1), 'yyyy-MM-dd'));
  };

  const handleNextDay = () => {
    setSelectedDate(format(addDays(new Date(selectedDate), 1), 'yyyy-MM-dd'));
  };

  const handleToday = () => {
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const handleDragStart = (e: React.DragEvent, userId: string, displayName: string, fromOutletId?: number, assignmentId?: string) => {
    if (!isAdmin) {
      e.preventDefault();
      return;
    }
    setDraggedStaff({ userId, displayName, fromOutletId, assignmentId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, outletId: number, isActive: boolean) => {
    if (!isAdmin) return;
    // Prevent dragging into inactive branches
    if (!isActive) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverOutletId(outletId);
  };

  const handleDragLeave = () => {
    setDragOverOutletId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetOutletId: number, isActive: boolean) => {
    e.preventDefault();
    setDragOverOutletId(null);
    if (!isAdmin || !draggedStaff) return;

    // Prevent dropping into inactive branches
    if (!isActive) {
      showToast('Cannot assign staff to an inactive branch. Please activate it first.', 'error');
      setDraggedStaff(null);
      return;
    }

    try {
      // If dragging from another outlet, first delete the old assignment
      if (draggedStaff.assignmentId) {
        await deleteAssignment(draggedStaff.assignmentId);
      }

      // Create new assignment
      await createAssignment({
        user_id: draggedStaff.userId,
        outlet_id: targetOutletId,
        assignment_date: selectedDate,
        is_manual: true
      });

      showToast(
        draggedStaff.fromOutletId
          ? `${draggedStaff.displayName} moved successfully`
          : `${draggedStaff.displayName} assigned successfully`,
        'success'
      );
      await loadSchedule();
    } catch (error) {
      showToast('Failed to assign staff', 'error');
      console.error(error);
    } finally {
      setDraggedStaff(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedStaff(null);
    setDragOverOutletId(null);
  };

  const handlePublishSchedule = async () => {
    if (!schedule || schedule.assignments.length === 0) {
      showToast('Cannot publish an empty schedule', 'error');
      return;
    }

    const action = isPublished ? 'republish' : 'publish';
    const message = isPublished
      ? 'Republish this schedule? Affected staff will be notified of any changes.'
      : 'Publish this schedule? Staff will be notified of their assignments.';

    if (!confirm(message)) {
      return;
    }

    setPublishing(true);
    try {
      const result = await publishSchedule(selectedDate);
      const successMessage = result.isRepublish
        ? 'Schedule republished successfully. Notifications sent to affected staff.'
        : 'Schedule published successfully. Notifications sent to all assigned staff.';
      showToast(successMessage, 'success');
      await loadSchedule();
    } catch (error) {
      showToast(`Failed to ${action} schedule`, 'error');
      console.error(error);
    } finally {
      setPublishing(false);
    }
  };

  const handleToggleOutlet = async (outletId: number, currentStatus: boolean) => {
    if (!isAdmin) return;

    // Optimistic update
    setSchedule(prev => {
      if (!prev) return null;
      return {
        ...prev,
        outlet_coverage: prev.outlet_coverage.map(outlet =>
          outlet.outlet_id === outletId
            ? { ...outlet, is_active: !currentStatus }
            : outlet
        )
      };
    });

    try {
      await toggleOutlet(outletId, !currentStatus);
      showToast(`Outlet ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      // Reload schedule to ensure consistency
      await loadSchedule();
    } catch (error) {
      showToast('Failed to toggle outlet status', 'error');
      console.error(error);
      // Revert optimistic update on error
      await loadSchedule();
    }
  };

  const getTrafficLevelColor = (level: string) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    return parts.slice(0, 2).map(part => part[0]?.toUpperCase() || '').join('');
  };

  if (loading && !schedule) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading schedule...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Staff Scheduler</h1>
        <p className="text-gray-600">Manage daily staff assignments across outlets</p>
      </div>

      {/* Date Navigation */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={handlePreviousDay}
            className="w-full sm:w-auto px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            ← Previous
          </button>

          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            />
            <button
              onClick={handleToday}
              className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              Today
            </button>
          </div>

          <button
            onClick={handleNextDay}
            className="w-full sm:w-auto px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="w-full sm:w-auto">
              <div className="flex items-center gap-3">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Schedule for {format(new Date(selectedDate), 'MMMM dd, yyyy')}
                </h2>
                {isPublished && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Published
                  </span>
                )}
                {!isPublished && schedule && schedule.assignments.length > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Draft
                  </span>
                )}
              </div>
              {schedule && (
                <p className="text-sm text-gray-600 mt-1">
                  {schedule.assignments.length} staff assigned • {schedule.unassigned_staff.length} unassigned
                </p>
              )}
              {publicationInfo && (
                <p className="text-xs text-gray-500 mt-1">
                  Published by {publicationInfo.published_by_name} • Version {publicationInfo.version} • {format(new Date(publicationInfo.published_at), 'MMM dd, yyyy h:mm a')}
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
              {isAdmin && !draggedStaff && (
                <p className="text-xs sm:text-sm text-gray-500 italic">
                  💡 Drag and drop staff to assign or move them
                </p>
              )}
              {isAdmin && draggedStaff && (
                <p className="text-xs sm:text-sm text-blue-600 font-medium">
                  📍 Drop {draggedStaff.displayName} on a branch card
                </p>
              )}
              {isAdmin && (
                <>
                  <button
                    onClick={handleGenerateSchedule}
                    disabled={generating || publishing}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition font-medium text-sm sm:text-base whitespace-nowrap"
                  >
                    {generating ? 'Generating...' : '🔄 Generate Schedule'}
                  </button>
                  <button
                    onClick={handlePublishSchedule}
                    disabled={publishing || generating || !schedule || schedule.assignments.length === 0}
                    className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-sm sm:text-base whitespace-nowrap transition ${isPublished
                      ? 'bg-amber-600 hover:bg-amber-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                      } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                  >
                    {publishing ? 'Publishing...' : isPublished ? '🔄 Republish' : '📢 Publish Schedule'}
                  </button>
                </>
              )}
            </div>
          </div>
          {isAdmin && !isPublished && schedule && schedule.assignments.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This schedule is not published yet. Staff cannot see it until you click "Publish Schedule".
              </p>
            </div>
          )}
        </div>
      </div>

      {!isAdmin && !isPublished && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-lg text-blue-800">
            <strong>No schedule published yet</strong>
          </p>
          <p className="text-sm text-blue-600 mt-2">
            The schedule for {format(new Date(selectedDate), 'MMMM dd, yyyy')} has not been published yet. Please check back later.
          </p>
        </div>
      )}

      {schedule && schedule.assignments.length > 0 && (
        <>
          {/* Outlet Coverage */}
          {(() => {
            // Filter outlets based on user role - hide inactive for staff
            const visibleCoverage = schedule.outlet_coverage.filter(
              coverage => isAdmin || (coverage.is_active ?? true)
            );

            // Special outlets that should be on bottom row (max 2 columns)
            const specialOutletNames = ['Delivery Team', 'Thirsty Office'];

            // Separate regular and special outlets
            const regularOutlets = visibleCoverage.filter(
              coverage => !specialOutletNames.includes(coverage.outlet_name)
            );
            const specialOutlets = visibleCoverage.filter(
              coverage => specialOutletNames.includes(coverage.outlet_name)
            );

            return (
              <>
                {/* Regular outlets - 3 columns */}
                {regularOutlets.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    {regularOutlets.map((coverage: OutletCoverage) => (
                      <div
                        key={coverage.outlet_id}
                        onDragOver={(e) => handleDragOver(e, coverage.outlet_id, coverage.is_active ?? true)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, coverage.outlet_id, coverage.is_active ?? true)}
                        className={`bg-white rounded-lg shadow-md p-4 sm:p-6 border-l-4 transition-all ${!(coverage.is_active ?? true)
                          ? 'border-gray-400 opacity-75 bg-gray-100 cursor-not-allowed'
                          : coverage.is_complete
                            ? 'border-green-500'
                            : 'border-red-500'
                          } ${isAdmin && draggedStaff && dragOverOutletId === coverage.outlet_id && (coverage.is_active ?? true)
                            ? 'ring-4 ring-blue-500 ring-opacity-75 bg-blue-50 scale-[1.02]'
                            : isAdmin && draggedStaff && (coverage.is_active ?? true)
                              ? 'ring-2 ring-blue-200 ring-opacity-50'
                              : isAdmin && draggedStaff && !(coverage.is_active ?? true)
                                ? 'ring-2 ring-red-300 ring-opacity-50'
                                : ''
                          }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                              {coverage.outlet_name}
                            </h3>
                            {isAdmin ? (
                              <button
                                onClick={() => handleToggleOutlet(coverage.outlet_id, coverage.is_active ?? true)}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 transition ${(coverage.is_active ?? true)
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  }`}
                              >
                                {(coverage.is_active ?? true) ? '✓ Active' : '✕ Inactive'}
                              </button>
                            ) : (
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${getTrafficLevelColor(coverage.traffic_level)}`}>
                                {coverage.traffic_level.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${coverage.is_complete ? 'text-green-600' : 'text-red-600'}`}>
                              {coverage.staff_assigned}/{coverage.staff_required}
                            </div>
                            <div className="text-xs text-gray-500">Staff</div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {coverage.assigned_staff.map((staff) => (
                            <div
                              key={staff.id}
                              draggable={isAdmin}
                              onDragStart={(e) => handleDragStart(e, staff.user_id, staff.staff_name, coverage.outlet_id, staff.id)}
                              onDragEnd={handleDragEnd}
                              className={`flex items-center justify-between p-2 bg-gray-50 rounded transition-all ${isAdmin ? 'cursor-move hover:bg-gray-100 hover:shadow-sm' : ''
                                } ${draggedStaff?.assignmentId === staff.id ? 'opacity-50' : ''}`}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                {isAdmin && (
                                  <span className="text-gray-400 text-xs">⋮⋮</span>
                                )}
                                <div className="font-medium text-sm">{staff.staff_name}</div>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={() => handleRemoveAssignment(staff.id)}
                                  className="text-red-600 hover:text-red-800 text-xs px-2 py-1 flex-shrink-0"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}

                          {coverage.assigned_staff.length === 0 && (
                            <div className="text-sm text-gray-400 italic p-2">
                              No staff assigned
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Special outlets (Delivery Team, Thirsty Office) - max 2 columns */}
                {specialOutlets.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    {specialOutlets.map((coverage: OutletCoverage) => (
                      <div
                        key={coverage.outlet_id}
                        onDragOver={(e) => handleDragOver(e, coverage.outlet_id, coverage.is_active ?? true)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, coverage.outlet_id, coverage.is_active ?? true)}
                        className={`bg-white rounded-lg shadow-md p-4 sm:p-6 border-l-4 transition-all ${!(coverage.is_active ?? true)
                          ? 'border-gray-400 opacity-75 bg-gray-100 cursor-not-allowed'
                          : coverage.is_complete
                            ? 'border-green-500'
                            : 'border-red-500'
                          } ${isAdmin && draggedStaff && dragOverOutletId === coverage.outlet_id && (coverage.is_active ?? true)
                            ? 'ring-4 ring-blue-500 ring-opacity-75 bg-blue-50 scale-[1.02]'
                            : isAdmin && draggedStaff && (coverage.is_active ?? true)
                              ? 'ring-2 ring-blue-200 ring-opacity-50'
                              : isAdmin && draggedStaff && !(coverage.is_active ?? true)
                                ? 'ring-2 ring-red-300 ring-opacity-50'
                                : ''
                          }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                              {coverage.outlet_name}
                            </h3>
                            {isAdmin ? (
                              <button
                                onClick={() => handleToggleOutlet(coverage.outlet_id, coverage.is_active ?? true)}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 transition ${(coverage.is_active ?? true)
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  }`}
                              >
                                {(coverage.is_active ?? true) ? '✓ Active' : '✕ Inactive'}
                              </button>
                            ) : (
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${getTrafficLevelColor(coverage.traffic_level)}`}>
                                {coverage.traffic_level.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${coverage.is_complete ? 'text-green-600' : 'text-red-600'}`}>
                              {coverage.staff_assigned}/{coverage.staff_required}
                            </div>
                            <div className="text-xs text-gray-500">Staff</div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {coverage.assigned_staff.map((staff) => (
                            <div
                              key={staff.id}
                              draggable={isAdmin}
                              onDragStart={(e) => handleDragStart(e, staff.user_id, staff.staff_name, coverage.outlet_id, staff.id)}
                              onDragEnd={handleDragEnd}
                              className={`flex items-center justify-between p-2 bg-gray-50 rounded transition-all ${isAdmin ? 'cursor-move hover:bg-gray-100 hover:shadow-sm' : ''
                                } ${draggedStaff?.assignmentId === staff.id ? 'opacity-50' : ''}`}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                {isAdmin && (
                                  <span className="text-gray-400 text-xs">⋮⋮</span>
                                )}
                                <div className="font-medium text-sm">{staff.staff_name}</div>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={() => handleRemoveAssignment(staff.id)}
                                  className="text-red-600 hover:text-red-800 text-xs px-2 py-1 flex-shrink-0"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}

                          {coverage.assigned_staff.length === 0 && (
                            <div className="text-sm text-gray-400 italic p-2">
                              No staff assigned
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}

          {isPublished && (
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Viewed by</p>
                    <p className="text-xs text-gray-500">
                      {viewersLoading
                        ? 'Updating...'
                        : scheduleViewers.length === 0
                          ? 'No staff has viewed this published schedule yet'
                          : `${scheduleViewers.length} staff viewed this schedule`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  {scheduleViewers.length > 0 ? (
                    <div className="flex items-center">
                      {scheduleViewers.length > 5 && (
                        <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center border-2 border-white shadow-sm">
                          +{scheduleViewers.length - 5}
                        </div>
                      )}
                      {scheduleViewers.slice(0, 5).map((viewer, index) => (
                        <div
                          key={viewer.user_id}
                          className={`w-9 h-9 rounded-full border-2 border-white shadow-sm bg-gray-200 overflow-hidden flex items-center justify-center text-xs font-semibold text-gray-600 ${index !== 0 ? '-ml-3' : ''}`}
                        >
                          {viewer.photo_url ? (
                            <img
                              src={viewer.photo_url}
                              alt={viewer.display_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span>{getInitials(viewer.display_name)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">No viewers yet</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Unassigned Staff */}
          {schedule.unassigned_staff.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                Unassigned Staff ({schedule.unassigned_staff.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {schedule.unassigned_staff.map((staff) => (
                  <div
                    key={staff.user_id}
                    draggable={isAdmin}
                    onDragStart={(e) => handleDragStart(e, staff.user_id, staff.display_name)}
                    onDragEnd={handleDragEnd}
                    className={`p-3 border border-gray-200 rounded-lg transition-all ${isAdmin ? 'cursor-move hover:bg-blue-50 hover:border-blue-300 hover:shadow-md' : ''
                      } ${draggedStaff?.userId === staff.user_id ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      {isAdmin && (
                        <span className="text-gray-400 text-xs">⋮⋮</span>
                      )}
                      <div className="font-medium text-gray-900 text-sm">{staff.display_name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StaffSchedulerPage;
